import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Search, Radio, ShoppingBag, Megaphone } from 'lucide-react';
import api from '@/lib/api';
import SafeImg from '@/components/SafeImg';
import OptionSheet from '@/components/OptionSheet';

const won = (n) => `${(Number(n) || 0).toLocaleString('ko-KR')}원`;

/**
 * BroadcastLiveGrid
 * -----------------
 * Consumer-facing grid used both on the seller profile and on /live/{sid}.
 * Renders numbered slot cards (① ② ③ …) for a single broadcast, polls the
 * public endpoint every 4s, and reuses the shared <OptionSheet /> for
 * add-to-cart / buy-now — meaning zero new order plumbing.
 *
 * Props
 *   broadcastId    (string, required)   which broadcast to show
 *   showTitle      (bool, default true) render the "오늘 라이브 상품" header
 *   compactSearch  (bool, default false) render search inline (profile),
 *                                        false = full-width (live page)
 *   pollMs         (number, default 4000)
 *   emptyText      (string)             message when the broadcast has no links
 */
const BroadcastLiveGrid = ({
  broadcastId, showTitle = true, compactSearch = false,
  pollMs = 4000, emptyText,
}) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [sheetProduct, setSheetProduct] = useState(null);
  const [sheetIntent, setSheetIntent] = useState('buy');
  const [q, setQ] = useState('');
  const [gbMap, setGbMap] = useState({}); // Phase 5.9: product_id → group_buy
  const timer = useRef(null);
  const [params, setParams] = useSearchParams();

  // Poll live-products every pollMs. Immediate refetch on broadcast change.
  useEffect(() => {
    if (!broadcastId) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await api.get(`/public/broadcasts/${broadcastId}/live-products`);
        if (!cancelled) { setData(r.data); setError(false); }
      } catch (e) {
        if (!cancelled) { setError(true); }
      }
    };
    poll();
    timer.current = setInterval(poll, pollMs);
    return () => { cancelled = true; if (timer.current) clearInterval(timer.current); };
  }, [broadcastId, pollMs]);

  // Phase 5.9 · items 로드되면 활성 공동구매를 병렬 조회 (product_id 별 · silent fail).
  useEffect(() => {
    const items = data?.items || [];
    if (items.length === 0) { setGbMap({}); return; }
    let cancelled = false;
    (async () => {
      const out = {};
      await Promise.all(items.map(async (it) => {
        try {
          const r = await api.get(`/public/group-buys/product/${it.product_id}`);
          if (r.data?.group_buy) out[it.product_id] = r.data.group_buy;
        } catch { /* silent */ }
      }));
      if (!cancelled) setGbMap(out);
    })();
    return () => { cancelled = true; };
  }, [data?.items]);

  const items = data?.items || [];
  const activeId = data?.active_product_id;
  // Phase 5.4+ · 공개모드. 기본 'all' = 기존 동작. 'introduced_only' 는 소개 전 상품 블라인드.
  const visibilityMode = data?.product_visibility_mode || 'all';
  const isBlinded = (it) => visibilityMode === 'introduced_only'
    && !it.is_introduced
    && it.product_id !== activeId;

  // Open the option sheet for a given slot number (1-indexed).
  const openBySlot = async (slot) => {
    const item = items.find((it) => it.slot_number === slot);
    if (!item) { toast.error(`${slot}번 상품을 찾을 수 없습니다.`); return; }
    if (isBlinded(item)) { toast.error(`${slot}번 상품은 아직 소개 전입니다.`); return; }
    if ((item.sale_status || '판매 중') === '품절') { toast.error(`${slot}번 상품은 품절입니다.`); return; }
    try {
      const r = await api.get(`/products/${item.product_id}`);
      // Preserve broadcast_id so OptionSheet forwards it into the order.
      setSheetProduct({ ...r.data, broadcast_id: broadcastId });
      setSheetIntent(item.product_id === activeId ? 'buy' : 'buy');
    } catch (e) { toast.error('상품 정보를 불러오지 못했습니다.'); }
  };

  // Handle ?slot=N deep-link. Fires once items load; then we strip the param
  // from the URL so a back-nav or reload doesn't re-open the sheet.
  const consumedSlot = useRef(false);
  useEffect(() => {
    if (consumedSlot.current) return;
    if (!items.length) return;
    const raw = params.get('slot');
    if (!raw) return;
    const n = parseInt(raw, 10);
    if (Number.isFinite(n)) {
      consumedSlot.current = true;
      openBySlot(n);
      const next = new URLSearchParams(params);
      next.delete('slot');
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line
  }, [items, params]);

  const submitSlotSearch = (e) => {
    e?.preventDefault?.();
    const n = parseInt(String(q).trim(), 10);
    if (!Number.isFinite(n) || n < 1) { toast.error('숫자를 입력하세요.'); return; }
    openBySlot(n);
  };

  if (error) return null;
  if (!broadcastId) return null;

  return (
    <section data-testid="broadcast-live-grid">
      {showTitle && (
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <Radio className="text-[#FF3B30]" size={20} />
          <h2 className="text-2xl font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>오늘 라이브 상품</h2>
          <span className="text-xs text-gray-500">{items.length}개</span>

          <form onSubmit={submitSlotSearch}
            className={`inline-flex items-center gap-1 ml-auto ${compactSearch ? '' : 'w-full sm:w-auto'}`}
            data-testid="slot-search">
            <label htmlFor="slot-input" className="text-[11px] font-bold text-gray-500 shrink-0">상품번호</label>
            <input id="slot-input" type="number" min={1} value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="4"
              className="w-16 px-2 py-1.5 rounded-lg border border-black/10 dark:border-white/10 text-sm text-center bg-transparent"
              data-testid="slot-search-input" />
            <button type="submit" className="px-3 py-1.5 rounded-lg bg-[#FF3B30] text-white text-xs font-bold inline-flex items-center gap-1" data-testid="slot-search-btn">
              <Search size={12} /> 찾기
            </button>
          </form>
        </div>
      )}

      {items.length === 0 ? (
        <p className="text-sm text-gray-500 py-8 text-center" data-testid="live-grid-empty">
          {emptyText || '등록된 라이브 상품이 없습니다.'}
        </p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4" data-testid="live-grid-list">
          {items.map((it) => {
            const isActive = it.product_id === activeId;
            const soldOut = (it.sale_status || '판매 중') === '품절';
            const blinded = isBlinded(it);
            const gb = gbMap[it.product_id];  // Phase 5.9
            return (
              <button type="button"
                key={it.product_id}
                onClick={() => openBySlot(it.slot_number)}
                disabled={soldOut || blinded}
                className={`relative text-left group rounded-2xl overflow-hidden border transition-all duration-200 ${
                  isActive
                    ? 'border-2 border-[#FF3B30] shadow-lg ring-2 ring-[#FF3B30]/20 bg-white dark:bg-[#141414]'
                    : 'border-black/5 dark:border-white/10 bg-white dark:bg-[#141414] hover:-translate-y-0.5 hover:shadow'
                } ${soldOut ? 'opacity-70 cursor-not-allowed' : ''} ${blinded ? 'cursor-not-allowed' : ''}`}
                data-testid={
                  blinded ? `live-slot-upcoming-${it.slot_number}` :
                  isActive ? `live-slot-active-${it.slot_number}` : `live-slot-${it.slot_number}`}
              >
                {/* Big slot number — always visible so viewers can see the running order */}
                <span
                  className={`absolute top-2 left-2 z-10 w-9 h-9 rounded-full flex items-center justify-center text-base font-black shadow ${
                    isActive ? 'bg-[#FF3B30] text-white' : blinded ? 'bg-black/60 text-white' : 'bg-black text-white'
                  }`}
                  aria-label={`${it.slot_number}번 상품`}
                  data-testid={`slot-number-${it.slot_number}`}
                >
                  {it.slot_number}
                </span>

                {isActive && (
                  <span className="absolute top-2 right-2 z-10 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-[#FF3B30] text-white text-[9px] font-black animate-pulse" data-testid={`live-badge-${it.slot_number}`}>
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-white" /> LIVE · 지금 소개중
                  </span>
                )}

                {!isActive && blinded && (
                  <span className="absolute top-2 right-2 z-10 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/70 text-white text-[10px] font-black" data-testid={`upcoming-badge-${it.slot_number}`}>
                    곧 진행
                  </span>
                )}

                {soldOut && !blinded && (
                  <span className="absolute inset-x-0 top-1/2 -translate-y-1/2 z-10 text-center">
                    <span className="inline-block px-3 py-1 rounded-full bg-black/70 text-white text-xs font-black">품절</span>
                  </span>
                )}

                {/* Phase 5.9 · 공동구매 배지 (좌측 상단 · 소개 카드 위) */}
                {gb && !blinded && !soldOut && (
                  <div className="absolute left-2 top-2 z-10 px-2 py-1 rounded-full bg-orange-500 text-white text-[10px] font-black inline-flex items-center gap-1 shadow-lg" data-testid={`gb-badge-${it.slot_number}`}>
                    🔥 공동구매 {gb.current_count}/{gb.target}
                  </div>
                )}

                <div className="aspect-square overflow-hidden bg-gray-100 dark:bg-gray-800">
                  <SafeImg src={it.image} kind="product" alt={blinded ? '곧 진행 상품' : it.name}
                    className={`w-full h-full object-cover ${!soldOut && !blinded ? 'group-hover:scale-105' : ''} ${blinded ? 'blur-md scale-105 opacity-70' : ''} transition-transform duration-300`} />
                </div>
                <div className="p-3">
                  <p className={`text-sm font-bold line-clamp-2 mb-1 min-h-[2.5rem] ${blinded ? 'text-gray-400 select-none' : ''}`}>
                    {blinded ? '곧 공개 예정' : it.name}
                  </p>
                  <div className="flex items-baseline gap-1.5 mb-2">
                    {blinded ? (
                      <span className="text-xs text-gray-400">소개 후 공개</span>
                    ) : gb ? (
                      <div className="w-full">
                        <div className="flex items-baseline gap-1.5">
                          <span className="text-[9px] text-orange-500 font-black">공동가</span>
                          <span className="text-sm font-black tracking-tight text-orange-600" style={{ fontFamily: 'Manrope, sans-serif' }}>{won(gb.current_price)}</span>
                          {gb.current_discount > 0 && gb.mode === 'percent' && (
                            <span className="text-[9px] text-orange-500 font-bold line-through">{won(gb.base_price)}</span>
                          )}
                        </div>
                        {gb.next_tier && (
                          <p className="text-[9px] text-gray-500 mt-0.5">
                            {gb.next_tier.min_participants - gb.current_count}명 더 → {gb.mode === 'percent' ? `${gb.next_tier.discount}% 할인` : `${won(gb.next_tier.discount)} 할인`}
                          </p>
                        )}
                      </div>
                    ) : (
                      <>
                        {it.discount_rate > 0 && <span className="text-[#FF3B30] text-xs font-black">-{it.discount_rate}%</span>}
                        <span className="text-sm font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>{won(it.sale_price)}</span>
                      </>
                    )}
                  </div>
                  <span className={`w-full inline-flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-bold rounded-md ${
                    blinded
                      ? 'bg-gray-100 dark:bg-white/5 text-gray-400'
                      : soldOut
                        ? 'bg-gray-200 dark:bg-white/10 text-gray-500'
                        : isActive
                          ? 'bg-[#FF3B30] text-white'
                          : 'bg-black text-white dark:bg-white dark:text-black group-hover:bg-[#FF3B30] group-hover:text-white'
                  }`}>
                    {blinded ? '곧 진행' : soldOut ? '품절' : (isActive ? <><Megaphone size={11} /> 바로구매</> : <><ShoppingBag size={11} /> 옵션 선택</>)}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      <OptionSheet
        product={sheetProduct}
        open={!!sheetProduct}
        intent={sheetIntent}
        onClose={() => setSheetProduct(null)}
      />
    </section>
  );
};

export default BroadcastLiveGrid;
